import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Sun, Sunset } from "lucide-react";
import { api } from "@/lib/api";

const DateChip = ({ d, active, onClick }) => {
  const dt = new Date(`${d.date}T00:00:00`);
  const disabled = d.closed || d.available_count === 0;
  return (
    <button type="button" disabled={disabled} onClick={onClick} data-testid={`date-chip-${d.date}`} title={d.reason || ""}
      className={`snap-start shrink-0 w-[68px] rounded-2xl border py-2.5 text-center transition-[background-color,border-color,transform] duration-200 ${
        active ? "bg-teal border-teal text-white" : disabled ? "bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed line-through decoration-slate-300" : "bg-white border-slate-200 text-ink hover:border-teal/50 hover:-translate-y-0.5"}`}>
      <span className="block text-[11px] uppercase tracking-wide">{dt.toLocaleDateString("en-IN", { weekday: "short" })}</span>
      <span className="block font-display text-xl font-semibold leading-tight">{dt.getDate()}</span>
      <span className="block text-[10px]">{d.today_off ? "Today" : d.closed ? "Closed" : d.available_count === 0 ? "Full" : dt.toLocaleDateString("en-IN", { month: "short" })}</span>
    </button>
  );
};

const SlotGroup = ({ title, icon: Icon, slots, slot, setSlot }) => {
  if (!slots.length) return null;
  return (
    <div>
      <p className="flex items-center gap-1.5 text-xs font-semibold text-ink-muted mb-2"><Icon className="h-3.5 w-3.5" />{title} ({slots[0].label} – {slots[slots.length - 1].label})</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {slots.map((s) => {
          const free = s.status === "available";
          const active = slot?.time === s.time;
          return (
            <button key={s.time} type="button" disabled={!free} onClick={() => setSlot(s)} data-testid={`slot-${s.time}`} data-status={s.status}
              className={`h-11 rounded-xl border text-sm font-medium transition-colors ${active ? "bg-teal text-white border-teal" : free ? "bg-white border-slate-200 text-ink hover:border-teal hover:bg-mint/50" : "bg-slate-50 border-slate-100 text-slate-300 line-through cursor-not-allowed"}`}>
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const DateTimeStep = ({ date, setDate, slot, setSlot, refreshKey, reasonTitle, onBack, onNext, backLabel = "Change reason", nextLabel, title = "Step 2 · Pick a date & time", excludeToday = false }) => {
  const [days, setDays] = useState(null);
  const [day, setDay] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/availability").then((r) => {
      if (!live) return;
      const list = excludeToday ? r.data.map((d, i) => (i === 0 ? { ...d, closed: true, today_off: true } : d)) : r.data;
      setDays(list);
      const open = (ds) => list.some((d) => d.date === ds && !d.closed && d.available_count > 0);
      const first = list.find((d) => !d.closed && d.available_count > 0);
      setDate((prev) => (prev && open(prev) ? prev : first ? first.date : null));
    });
    return () => { live = false; };
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!date) { setDay(null); return; }
    let live = true;
    setDay(null);
    api.get("/slots", { params: { date } }).then((r) => { if (live) setDay(r.data); });
    return () => { live = false; };
  }, [date, refreshKey]);

  const slots = day?.slots || [];
  const morning = slots.filter((s) => s.time < "14:00");
  const afternoon = slots.filter((s) => s.time >= "14:00");

  return (
    <div data-testid="datetime-step">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <button type="button" onClick={onBack} className="text-xs text-teal inline-flex items-center gap-1 hover:underline" data-testid="datetime-back-button"><ArrowLeft className="h-3 w-3" /> {backLabel}</button>
      </div>
      {reasonTitle && <p className="text-xs text-ink-muted mb-3 truncate" data-testid="selected-reason-label">{reasonTitle}</p>}
      <div className="flex gap-2 overflow-x-auto no-scrollbar snap-x pb-2 -mx-1 px-1" data-testid="date-strip">
        {days ? days.map((d) => (
          <DateChip key={d.date} d={d} active={d.date === date} onClick={() => { setDate(d.date); setSlot(null); }} />
        )) : <Loader2 className="h-5 w-5 animate-spin text-ink-muted my-6" />}
      </div>
      <div className="mt-4 space-y-4 min-h-[140px]" data-testid="slot-list">
        {date && !day && <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>}
        {days && !date && <p className="text-sm text-ink-muted py-6 text-center">No open slots in the next 14 days. Please call the clinic.</p>}
        {day && !slots.some((s) => s.status === "available") && <p className="text-sm text-ink-muted text-center py-4" data-testid="no-slots-message">No slots left on this day — try another date.</p>}
        <SlotGroup title="Morning Slots" icon={Sun} slots={morning} slot={slot} setSlot={setSlot} />
        <SlotGroup title="Afternoon Slots" icon={Sunset} slots={afternoon} slot={slot} setSlot={setSlot} />
      </div>
      <button type="button" disabled={!slot} onClick={onNext} data-testid="datetime-continue-button"
        className="mt-6 w-full h-12 rounded-full bg-teal text-white font-semibold disabled:opacity-40 hover:bg-teal-dark transition-colors">
        {nextLabel || (slot ? `Continue with ${slot.label}` : "Select a time slot")}
      </button>
    </div>
  );
};
